import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/inputs/input';
import { InputSelect } from '@/components/ui/inputs/input-select';
import { InputDatepicker } from '@/components/ui/inputs/input-datepicker';
import { useForm, Controller } from 'react-hook-form';
import { getItemCategoryService } from '@/services/ItemCategoryServices';
import { getItemService } from '@/services/ItemServices';
import validationErrors from '@/lib/validationErrors';
import { useSelector, useDispatch } from 'react-redux';

const DataTableFilter = ({ onChangeFilter }) => {
    const selectRef = useRef(null);
    const dispatch = useDispatch();
    const {
        register,
        setValue,
        getValues,
        formState: { errors },
        handleSubmit,
        control,
        reset,
    } = useForm({
        defaultValues: {
            item_category_id: '',
            item_type: '',
            _id: '',
            createdAt: '',
        },
        mode: 'onSubmit',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [itemCategoriesOptionList, setItemCategoriesOptionList] = useState([]);
    const [itemOptionList, setItemOptionList] = useState([]);
    const itemTypeOptionList = [
        { value: 1, label: 'Veg' },
        { value: 2, label: 'Non Veg' },
    ];

    useEffect(() => {
        getItemCategory();
        getItems();
    }, []);

    const convertUndefinedToBlank = (obj) => {
        return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, value === undefined ? '' : value]));
    };

    const clearSelection = () => {
        if (selectRef.current) {
            selectRef.current.clearValue();
        }
    };

    const getItemCategory = () => {
        setIsLoading(true);
        dispatch(
            getItemCategoryService({
                search: '',
                page: 1,
                limit: 100,
                extra_filter: {},
            })
        )
            .then((res) => {
                var itemCategoryDataArr = res?.data?.data?.data;
                var itemCategoryOptionArray = [];
                itemCategoryDataArr.forEach((element) => {
                    itemCategoryOptionArray.push({
                        value: element?._id,
                        label: element?.name,
                    });
                });
                setItemCategoriesOptionList(itemCategoryOptionArray);
                setIsLoading(false);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsLoading(false);
            });
    };

    const getItems = () => {
        setIsLoading(true);
        dispatch(
            getItemService({
                search: '',
                page: 1,
                limit: 1000,
                extra_filter: {},
            })
        )
            .then((res) => {
                var itemDataArr = res?.data?.data?.data;
                var itemOptionArray = [];
                itemDataArr.forEach((element) => {
                    itemOptionArray.push({
                        value: element?._id,
                        label: element?.name,
                    });
                });
                setItemOptionList(itemOptionArray);
                setIsLoading(false);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsLoading(false);
            });
    };

    const formReset = async () => {
        try {
            var formData = getValues();
            for (const key in formData) {
                await setValue(key, '');
            }
            await reset();
            await clearSelection();
            await onChangeFilter('extra_filter', {});
        } catch (error) {
            console.error('Error in onReset:', error);
        }
    };

    const onSubmit = async (data) => {
        try {
            console.log(data);
            const filterData = await convertUndefinedToBlank(data);
            await onChangeFilter('extra_filter', filterData);
        } catch (error) {
            console.error('Error in onSubmit:', error);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-5 mb-5">
            <div className="row">
                <div className="col-12 sm:col-6 md:col-4 xl:col-3">
                    <Controller
                        control={control}
                        name="item_category_id"
                        render={({ field }) => (
                            <InputSelect
                                {...field}
                                ref={selectRef}
                                id={'item_category_id'}
                                size="sm"
                                labelStyleClassName={'db-field-title after:hidden'}
                                labelText={'Category'}
                                value={itemCategoriesOptionList.find((data) => data.value === getValues('item_category_id')) || null}
                                onChange={(e) => setValue('item_category_id', e)}
                                options={itemCategoriesOptionList}
                            />
                        )}
                    />
                </div>
                <div className="col-12 sm:col-6 md:col-4 xl:col-3">
                    <Controller
                        control={control}
                        name="status"
                        render={({ field }) => (
                            <InputSelect
                                {...field}
                                ref={selectRef}
                                id={'item_type'}
                                size="sm"
                                labelStyleClassName={'db-field-title after:hidden'}
                                labelText={'Type'}
                                value={itemTypeOptionList.find((data) => data.value === getValues('item_type')) || null}
                                onChange={(e) => setValue('item_type', e)}
                                options={itemTypeOptionList}
                            />
                        )}
                    />
                </div>
                <div className="col-12 sm:col-6 md:col-4 xl:col-3">
                    <Controller
                        control={control}
                        name="_id"
                        render={({ field }) => (
                            <InputSelect
                                {...field}
                                ref={selectRef}
                                id={'_id'}
                                size="sm"
                                labelStyleClassName={'db-field-title after:hidden'}
                                labelText={'Name'}
                                value={itemOptionList.find((data) => data.value === getValues('_id')) || null}
                                onChange={(e) => setValue('_id', e)}
                                options={itemOptionList}
                            />
                        )}
                    />
                </div>
                <div className="col-12 sm:col-6 md:col-4 xl:col-3">
                    <Controller
                        control={control}
                        name="createdAt"
                        render={({ field }) => (
                            <InputDatepicker
                                {...field}
                                onChange={(date) => setValue('createdAt', date)}
                                selected={getValues('createdAt')}
                                size="sm"
                                labelText={'Date'}
                                type="date"
                                id="createdAt"
                                className="db-field-control"
                                labelStyleClassName={'db-field-title after:hidden'}
                            />
                        )}
                    />
                </div>

                <div className="col-12">
                    <div className="flex flex-wrap gap-3 mt-4">
                        <button type="submit" className="db-btn py-2 text-white bg-primary">
                            <i className="lab lab-search-line lab-font-size-16"></i>
                            <span>Search</span>
                        </button>
                        <button type="button" onClick={formReset} className="db-btn py-2 text-white bg-gray-600">
                            <i className="lab lab-cross-line-2 lab-font-size-22"></i>
                            <span>Clear</span>
                        </button>
                    </div>
                </div>
            </div>
        </form>
    );
};

export default DataTableFilter;
